import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchGoogleWorkspaceConfig,
  saveGoogleWorkspaceConfig,
  removeGoogleWorkspaceConfig,
  probeDriveMembership,
} from "../api/integrations";

// Admin-only read: the caller gates on role, and the flag keeps a
// non-admin mount from ever issuing the request.
export function useGoogleWorkspaceConfig(enabled: boolean) {
  return useQuery({
    queryKey: ["integrations", "google-workspace"],
    queryFn: fetchGoogleWorkspaceConfig,
    staleTime: 60_000,
    enabled,
  });
}

// The two ids decide what Drive, Workspace and Sheets can reach, so the
// connections aggregate refreshes with this block.
function useGoogleWorkspaceInvalidation() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["integrations", "google-workspace"] }),
      queryClient.invalidateQueries({ queryKey: ["integrations", "status"] }),
    ]);
  };
}

// One mutation key for every write, so the surface holding the form can
// ask "is a call in flight?" without owning the mutations.
const WORKSPACE_WRITE = ["integrations", "google-workspace"];

export function useSaveGoogleWorkspaceConfig() {
  const invalidate = useGoogleWorkspaceInvalidation();
  return useMutation({
    mutationKey: WORKSPACE_WRITE,
    mutationFn: saveGoogleWorkspaceConfig,
    onSuccess: invalidate,
  });
}

export function useRemoveGoogleWorkspaceConfig() {
  const invalidate = useGoogleWorkspaceInvalidation();
  return useMutation({
    mutationKey: WORKSPACE_WRITE,
    mutationFn: removeGoogleWorkspaceConfig,
    onSuccess: invalidate,
  });
}

/** A live check of the account's place in the Shared Drive. Busts both
    reads: the verdict lands on the Drive row and in the section alike. */
export function useProbeDriveMembership() {
  const invalidate = useGoogleWorkspaceInvalidation();
  return useMutation({
    mutationKey: WORKSPACE_WRITE,
    mutationFn: probeDriveMembership,
    onSuccess: invalidate,
  });
}
